import Chart from 'chart.js';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import numeral from 'numeral';
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import swal from 'sweetalert2';
import {
  fetchSequences,
  fetchStatsMailPush,
} from '../../../../../actions/entities/statistics/mailPush';
import earthFlag from '../../../../../assets/img/other/flag-earth.svg';
import Filters from '../../../../../components/Filters';
import FilterPeriod from '../../../../../components/Filters/Period';
import FilterSelect from '../../../../../components/Filters/Select';
import Loader from '../../../../../components/Loader';
import {getIncrease} from '../../../../../helpers/stats';
import './index.css';

const cols = {
  id: 'ID',
  apiSent: 'API Sent',
  sent: 'Sent',
  sentCompareToPreviousSeason: 'vs S-1',
  deliveredRate: 'Deliver rate',
  opened: 'Opened',
  openedRate: 'Open rate',
  clicked: 'Clicked',
  clickedCompareToPreviousSeason: 'vs S-1',
  clicksRate: 'Click rate',
  clicksRateCompareToPreviousSeason: 'vs S-1',
};

const charts = {
  emailsChart: {},
  pushesChart: {},
  templateChart: {},
};

class StatsMailPushWidget extends React.Component {
  constructor(props) {
    super(props);

    // We set the date to yesterday and we subtract 30 days
    const today = new Date();
    const end = moment(today.setDate(today.getDate())).format('YYYY-MM-DD');
    let start = moment(today.setDate(today.getDate() - 30)).format(
      'YYYY-MM-DD',
    );

    if (moment(start).isBefore('2019-03-01')) {
      start = '2019-03-01';
    }

    this.state = {
      filters: {
        language: 'ALL',
        period: {end, start},
        platform: 'both',
        sequences: [],
        app: props.app,
      },
      order: {key: 'id', value: 'asc'},
      isFetching: false,
      highlightRefreshBtn: true,
      sequences: undefined,
      selectedTemplate: null,
    };
  }

  updateFilters(filter) {
    this.setState({filters: {...this.state.filters, ...filter}});
  }

  notifyRefresh(value) {
    const {highlightRefreshBtn} = this.state;
    this.setState({
      highlightRefreshBtn: value !== undefined ? value : !highlightRefreshBtn,
    });
  }

  selectOrChangeOrder(key) {
    if (key === this.state.order.key)
      this.setState({
        order: {key, value: this.state.order.value === 'asc' ? 'desc' : 'asc'},
      });
    else {
      const newOrder = {key};
      // The switch is necessary for the default sortOrder each orderType
      switch (key) {
        case 'id':
          newOrder.value = 'asc';
          break;
        case 'sent':
          newOrder.value = 'desc';
          break;
        case 'sentCompareToPreviousSeason':
          newOrder.value = 'desc';
          break;
        case 'delivered':
          newOrder.value = 'desc';
          break;
        case 'opened':
          newOrder.value = 'desc';
          break;
        case 'clicked':
          newOrder.value = 'desc';
          break;
        case 'clicksRate':
          newOrder.value = 'desc';
          break;
        case 'clicksRateCompareToPreviousSeason':
          newOrder.value = 'desc';
          break;
        case 'percentCompareToPreviousSeason':
          newOrder.value = 'desc';
          break;
        default:
          newOrder.value = 'desc';
      }
      this.setState({order: newOrder});
    }
  }

  onError = error => {
    this.setState({isFetching: false});
    swal({
      title: `Oops...`,
      type: 'error',
      html:
        `Something went wrong !<br/>${error}<br/>` +
        `<pre><code>${JSON.stringify(error, undefined, 4)}</code></pre>`,
    });
  };

  fetchSequences() {
    const callBacks = {
      onError: this.onError,
      onRequest: () => this.setState({isFetching: true}),
      onSuccess: response => {
        this.setState({
          isFetching: false,
          sequences: response.map(s => ({text: s, value: s})),
        });
        this.fetchStats();
      },
    };

    if (!this.state.isFetching) {
      this.props.dispatch(fetchSequences(callBacks, this.state.filters.app));
    }
  }

  fetchStats() {
    const callBacks = {
      onError: this.onError,
      onRequest: () => this.setState({isFetching: true}),
      onSuccess: () => {
        this.setState({isFetching: false});
        this.notifyRefresh(false);
        this.updateAllCharts();
      },
    };

    if (!this.state.isFetching) {
      const {period, sequences, app} = this.state.filters;
      const payload = {
        endDate: moment(period.end).format('YYYY-MM-DD'),
        startDate: moment(period.start).format('YYYY-MM-DD'),
        sequences:
          sequences.length !== 0
            ? sequences
            : this.state.sequences.map(value => value.value),
        application: app,
      };
      this.props.dispatch(fetchStatsMailPush(payload, callBacks));
    }
  }

  customMergeForPlatform(data) {
    // Isolate byTemplateTotal for each platform
    const byTemplateTotal = {};
    map(cloneDeep(data), (dByP, p) => {
      byTemplateTotal[p] = {};
      map(dByP, (o, l) => (byTemplateTotal[p][l] = o.byTemplateTotal));
    });

    // Transform into array and specify templateId + templateType for each platform and languages
    map(byTemplateTotal, (dByP, p) => {
      map(
        dByP,
        (o, l) =>
          (dByP[l] = map(dByP[l], (template, id) => ({
            ...template,
            id,
            type: p,
          }))),
      );
    });

    // Concatenate the array between platform
    const concatenations = {};
    map(
      Object.values(byTemplateTotal)[0],
      (d, lang) => (concatenations[lang] = {byTemplateTotal: []}),
    );
    map(byTemplateTotal, dByP => {
      map(
        dByP,
        (d, lang) =>
          (concatenations[lang].byTemplateTotal =
            concatenations[lang].byTemplateTotal.concat(d)),
      );
    });

    // Add to data a new platform called 'both' with concatenations
    return {...data, both: concatenations};
  }

  getData(
    platform = this.state.filters.platform,
    language = this.state.filters.language,
    app = this.state.filters.app,
  ) {
    const {data} = this.props;
    if (isEmpty(data)) return false;

    const customData = this.customMergeForPlatform(data);
    const dataByPlatform = customData[platform];

    if (!dataByPlatform) return false;

    return dataByPlatform[language];
  }

  formatData(data) {
    const response = [];
    map(data, (o, id) => {
      if (!o.current) return false;
      const getRate = (rate, from) =>
        o.current[from] ? o.current[rate] / o.current[from] : 1;
      const compare = key =>
        o.previous ? getIncrease(o.current[key], o.previous[key], false) : inf;

      const inf = Infinity;
      const sentCompareToPreviousSeason = compare('sent');
      const deliveredRate = getRate('delivered', 'sent');
      const openedRate = getRate('opened', 'delivered');
      const clickedCompareToPreviousSeason = o.clicked && o.previous.clicked;
      const clicksRate = getRate('clicked', 'delivered');
      const clicksRatePrevious =
        o.previous && o.previous.delivered
          ? o.previous.clicked / o.previous.delivered
          : 0;
      const clicksRateCompareToPreviousSeason = clicksRatePrevious
        ? getIncrease(clicksRate, clicksRatePrevious, false)
        : inf;

      response.push({
        ...o.current,
        id: o.id || id,
        type: o.type,
        apiSent: o.current.api_sent,
        sentCompareToPreviousSeason,
        deliveredRate,
        openedRate,
        clicksRate,
        clickedCompareToPreviousSeason,
        clicksRatePrevious,
        clicksRateCompareToPreviousSeason,
      });
    });

    return response;
  }

  updateTotalChart(chart, data, yAxisID) {
    const {selectedTemplate} = this.state;
    const datasets = [
      {
        label: 'Sent',
        backgroundColor: '#dd483e',
        xAxisID: 'date',
        yAxisID,
        data: [],
      },
      {
        label: 'Delivered',
        backgroundColor: '#515eb7',
        xAxisID: 'date',
        yAxisID,
        data: [],
      },
      {
        label: 'Clicked',
        backgroundColor: '#50c92f',
        xAxisID: 'date',
        yAxisID,
        data: [],
      },
    ];

    if (
      chart === 'emailsChart' ||
      (chart === 'templateChart' &&
        selectedTemplate &&
        selectedTemplate.type === 'emails')
    ) {
      datasets.push({
        label: 'Opened',
        backgroundColor: '#edad3c',
        xAxisID: 'date',
        yAxisID,
        data: [],
      });
    }

    map(data, (stat, index) => {
      datasets[0].data.push({
        x: moment(index).format('dddd LL'),
        y: parseInt(stat.sent, 10),
      });
      datasets[1].data.push({
        x: moment(index).format('dddd LL'),
        y: parseInt(stat.delivered, 10),
      });
      datasets[2].data.push({
        x: moment(index).format('dddd LL'),
        y: parseInt(stat.clicked, 10),
      });
      if (
        chart === 'emailsChart' ||
        (chart === 'templateChart' &&
          selectedTemplate &&
          selectedTemplate.type === 'emails')
      ) {
        datasets[3].data.push({
          x: moment(index).format('dddd LL'),
          y: parseInt(stat.opened, 10),
        });
      }
    });

    if (!charts[chart].data) {
      StatsMailPushWidget.createChart(chart, yAxisID, datasets);
    } else {
      charts[chart].data.datasets = datasets;
      charts[chart].update();
    }
  }

  static createChart(id, yAxisID, datasets = []) {
    if (document.getElementById(id)) {
      charts[id] = new Chart(document.getElementById(id), {
        type: 'bar',
        data: {datasets},
        options: {
          scales: {
            xAxes: [
              {
                id: 'date',
                type: 'time',
                time: {
                  unit: 'day',
                },
                stacked: true,
              },
            ],
            yAxes: [
              {
                id: yAxisID,
                type: 'linear',
                stacked: true,
              },
            ],
          },
        },
      });
    }
  }

  updateAllCharts() {
    const {selectedTemplate} = this.state;

    const emailsData = this.getData('emails');
    const pushData = this.getData('pushNotifications');

    console.log(emailsData.totalByDays);

    this.updateTotalChart('emailsChart', emailsData.totalByDays, 'emails');
    this.updateTotalChart('pushesChart', pushData.totalByDays, 'push');

    if (selectedTemplate) {
      const data = this.getData(selectedTemplate.type);
      this.updateTotalChart(
        'templateChart',
        data.byTemplateByDays && data.byTemplateByDays[selectedTemplate.id],
        'emails & push',
      );
    }
  }

  componentDidMount() {
    StatsMailPushWidget.createChart('emailsChart', 'emails');
    StatsMailPushWidget.createChart('pushesChart', 'push');
    StatsMailPushWidget.createChart('templateChart', 'emails & push');
    this.fetchSequences();
  }

  componentDidUpdate(prevProps, prevState) {
    const {filters, selectedTemplate} = this.state;
    const filterHasChange =
      prevState.filters &&
      (filters.platform !== prevState.filters.platform ||
        filters.language !== prevState.filters.language);

    if (
      filterHasChange ||
      (selectedTemplate && selectedTemplate !== prevState.selectedTemplate)
    ) {
      this.updateAllCharts();
    }
  }

  renderTable() {
    const {order} = this.state;

    const data = this.getData();

    if (!data) return <p className="text-center px-3 pb-3">No data to show</p>;

    const byTemplateTotal = this.formatData(data.byTemplateTotal);

    return (
      <div className="table-responsive">
        <h4 className="text-right">
          <span className="text-email mr-4">
            <i className="fa fa-envelope" /> Email
          </span>
          <span className="text-push mr-4">
            <i className="fa fa-bell" /> Push
          </span>
        </h4>
        <table className="table table-striped">
          <thead>
            <tr>{this.renderThs(cols)}</tr>
          </thead>
          <tbody>
            {orderBy(byTemplateTotal, [order.key], [order.value]).map((t, i) =>
              StatsMailPushWidget.renderTr(t, i, () => {
                this.setState({selectedTemplate: t});
              }),
            )}
          </tbody>
        </table>
      </div>
    );
  }

  renderTotalTable() {
    const emailsData = this.getData('emails');
    const pushData = this.getData('pushNotifications');

    const emails =
      emailsData && this.formatData({EMAILS: emailsData.sequencesTotal})[0];
    const pushes =
      pushData && this.formatData({PUSH: pushData.sequencesTotal})[0];

    return (
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>{this.renderThs(cols, true)}</tr>
          </thead>
          <tbody>
            {emails && StatsMailPushWidget.renderTr(emails)}
            {pushes && StatsMailPushWidget.renderTr(pushes)}
          </tbody>
        </table>
      </div>
    );
  }

  static renderTr(o, key, onClick) {
    return (
      <tr key={key} onClick={onClick} role={onClick ? 'button' : 'default'}>
        <td
          className={classNames({
            'text-email': o.type === 'emails',
            'text-push': o.type === 'pushNotifications',
          })}>
          {o.id}
        </td>
        <td>{numeral(o.apiSent).format('0,0')}</td>
        <td>{numeral(o.sent).format('0,0')}</td>
        <td>
          {o.sentCompareToPreviousSeason === Infinity
            ? '+∞'
            : numeral(o.sentCompareToPreviousSeason).format('0,0 %')}
        </td>
        <td>{numeral(o.deliveredRate).format('0,0 %')}</td>
        <td>{numeral(o.opened).format('0,0')}</td>
        <td>{numeral(o.openedRate).format('0,0 %')}</td>
        <td>{numeral(o.clicked).format('0,0')}</td>
        <td>{numeral(o.clickedCompareToPreviousSeason).format('0,0')}</td>
        <td>{numeral(o.clicksRate).format('0,0 %')}</td>
        <td>
          {o.clicksRateCompareToPreviousSeason === Infinity
            ? '+∞'
            : numeral(o.clicksRateCompareToPreviousSeason).format('0,0 %')}
        </td>
      </tr>
    );
  }

  renderThs(cols, hideSort = false) {
    const {order} = this.state;
    return map(cols, (value, key) => (
      <th key={key}>
        <span
          onClick={() => !hideSort && this.selectOrChangeOrder(key)}
          className={classNames('orderBtn', {
            'text-primary': order.key === key && !hideSort,
          })}>
          {value}
          <span className={classNames('ml-1', {hidden: hideSort})}>
            <i
              className={classNames('fa', {
                'fa-sort': order.key !== key,
                [`fa-sort-${order.value}`]: order.key === key,
              })}
            />
          </span>
        </span>
      </th>
    ));
  }

  toggleLang(value) {
    const {
      filters: {language},
    } = this.state;

    if (language === value) {
      this.setState({filters: {...this.state.filters, language: 'ALL'}});
    } else {
      this.setState({filters: {...this.state.filters, language: value}});
    }
  }

  render() {
    const {isFetching, highlightRefreshBtn, selectedTemplate, filters} =
      this.state;

    if (!this.state.sequences) {
      return <Loader />;
    }

    return (
      <div id="stats-mailPush-widget">
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title pull-left">
              <i className="fa fa-envelope-open" />
              {` MailPush`}
            </h3>
          </div>
          <div className="box-body">
            <Filters>
              <FilterSelect
                label="Platform:"
                onChange={app => this.updateFilters({app})}
                options={this.props.availableFilters}
                initialValue={this.state.filters.app}
              />
              <div className="inline lang">
                <button
                  className={classNames('btn btn-box-tool', {
                    selected: filters.language === 'FR',
                  })}
                  onClick={() => this.toggleLang('FR')}>
                  <span className="flag-icon flag-icon-fr" />
                </button>
                <button
                  className={classNames('btn btn-box-tool', {
                    selected: filters.language === 'GB',
                  })}
                  onClick={() => this.toggleLang('GB')}>
                  <span className="flag-icon flag-icon-gb" />
                </button>
                <button
                  className={classNames('btn btn-box-tool', {
                    selected: filters.language === 'ES',
                  })}
                  onClick={() => this.toggleLang('ES')}>
                  <span className="flag-icon flag-icon-es" />
                </button>
                <button
                  className={classNames('btn btn-box-tool', {
                    selected: filters.language === 'OTHER',
                  })}
                  onClick={() => this.toggleLang('OTHER')}>
                  <img
                    src={earthFlag}
                    alt="Drapeau de la terre"
                    className="flag-img-btn"
                  />
                </button>
              </div>
              <FilterSelect
                label="Sequences:"
                onChange={sequences => {
                  this.updateFilters({sequences});
                  this.notifyRefresh(true);
                }}
                options={this.state.sequences}
                initialValue={this.state.filters.sequences}
                selectProps={{multiple: true}}
              />
              <FilterPeriod
                label="Date range:"
                onChange={period => {
                  this.updateFilters({period});
                  this.notifyRefresh(true);
                }}
                buttonGroup={{
                  icon: 'fa fa-refresh',
                  type: highlightRefreshBtn ? 'success' : 'default',
                  onClick: this.fetchStats.bind(this),
                }}
                initialValues={this.state.filters.period}
                datePickerProps={{
                  isOutsideRange: day => day.isBefore('2019-03-01'),
                }}
              />
            </Filters>
          </div>
          {isFetching && <Loader />}
          {this.renderTotalTable()}

          <div className="box-body pt-0">
            <div className="row">
              <div className="col-md-6">
                <h4>
                  Emails <small className="text-muted">totalByDays</small>
                </h4>
                <div className="chart">
                  <canvas id="emailsChart" />
                </div>
              </div>
              <div className="col-md-6">
                <h4>
                  Push <small className="text-muted">totalByDays</small>
                </h4>
                <div className="chart">
                  <canvas id="pushesChart" />
                </div>
              </div>
            </div>
          </div>

          <div className="box-body pt-0">
            <Filters>
              {[
                <FilterSelect
                  key="platform"
                  label="Platform:"
                  onChange={platform => this.updateFilters({platform})}
                  options={[
                    {value: 'both', text: 'Both'},
                    {value: 'emails', text: 'Email'},
                    {value: 'pushNotifications', text: 'Push Notification'},
                  ]}
                  initialValue={this.state.filters.platform}
                />,
              ]}
            </Filters>
          </div>

          {this.renderTable()}

          <div className="box-body pt-0">
            <h4>
              Template
              {selectedTemplate && (
                <small
                  className={classNames('ml-3', {
                    'text-email': selectedTemplate.type === 'emails',
                    'text-push': selectedTemplate.type === 'pushNotifications',
                    'text-muted': !selectedTemplate.type,
                  })}>
                  {selectedTemplate.id}
                </small>
              )}
            </h4>
            <div className="chart">
              <canvas id="templateChart" />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default connect(
  state => ({data: state.app.statistics.mailPush}),
  dispatch => ({dispatch}),
)(StatsMailPushWidget);

StatsMailPushWidget.propTypes = {
  app: PropTypes.string,
  availableFilters: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
};

StatsMailPushWidget.defaultProps = {
  availableFilters: [{value: 'mpg', text: 'MPG'}],
  app: 'mpg',
};



// WEBPACK FOOTER //
// ./src/pages/Monitoring/Statistics/Widgets/MailPush/index.js